import { useEffect, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { STATUS_LABEL, api, inr } from "@/lib/api";

// Current application fee (paise) as set by the admin; one shared request with retry; null while loading.
let feePromise = null;
const loadFee = (attempt = 0) =>
  api.get("/public-config").then((r) => r.data.application_fee).catch((e) => {
    if (attempt < 3) return new Promise((res) => setTimeout(res, 800)).then(() => loadFee(attempt + 1));
    feePromise = null;
    throw e;
  });

export const useFee = () => {
  const [fee, setFee] = useState(null);
  useEffect(() => {
    let alive = true;
    feePromise = feePromise || loadFee();
    feePromise.then((f) => alive && setFee(f)).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return fee;
};

export const feeText = (fee) => (fee ? inr(fee) : "…");

export const FeeNotice = () => {
  const fee = feeText(useFee());
  return (
  <div className="rounded-2xl border-2 border-amber-500 bg-amber-50 p-5 sm:p-6" data-testid="fee-notice">
    <div className="mb-3 flex items-center gap-2 text-amber-800">
      <AlertTriangle className="h-5 w-5" />
      <h3 className="font-display text-lg font-bold tracking-wide">APPLICATION FEE NOTICE</h3>
    </div>
    <div className="space-y-3 text-sm leading-relaxed text-amber-950">
      <p>The {fee} payment is an application processing/submission fee, where legally permitted. It is not a payment for a job, employment guarantee, interview guarantee, or guaranteed selection.</p>
      <p className="font-semibold">Payment of this fee does not guarantee employment.</p>
      <p>Please review the applicable fee, refund policy, eligibility requirements, and Terms &amp; Conditions before proceeding.</p>
    </div>
  </div>
  );
};

export const RecruitmentDisclaimer = ({ className = "" }) => (
  <div className={`flex gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 ${className}`} data-testid="recruitment-disclaimer">
    <Info className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
    <p>Final recruitment depends on eligibility, verification, interviews and the company's genuine recruitment requirements. BMI SP GROUP does not guarantee employment, selection or a particular salary to any applicant.</p>
  </div>
);

const TONE = {
  paid: "bg-emerald-100 text-emerald-800", submitted: "bg-emerald-100 text-emerald-800", selected: "bg-emerald-100 text-emerald-800",
  delivered: "bg-emerald-100 text-emerald-800", shortlisted: "bg-teal-100 text-teal-800", interview_scheduled: "bg-teal-100 text-teal-800",
  failed: "bg-red-100 text-red-700", rejected: "bg-red-100 text-red-700", cancelled: "bg-red-100 text-red-700",
  refunded: "bg-slate-200 text-slate-700", withdrawn: "bg-slate-200 text-slate-700",
};

export const StatusBadge = ({ status, testid }) => (
  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${TONE[status] || "bg-amber-100 text-amber-800"}`} data-testid={testid}>
    {STATUS_LABEL[status] || status}
  </span>
);
